/**
 * DWA Internal departments + dashboards catalogue.
 *
 * Hardcoded for now — moves to a backend-driven catalogue once Phase 2
 * (per-user dashboard access) lands and we need to filter visible
 * dashboards based on the user's client_access[dwa-internal].dashboards.
 *
 * Routing: flat aliases. `/dashboard/<dashboard.slug>` → renders the
 * dashboard directly. The department is the dashboard's home Hub (see
 * `src/hubs/hubs.ts`, which reads this list); it drives the Hub page
 * listing and the breadcrumb. Each dashboard slug is globally unique.
 *
 * Status semantics:
 *   'live'        — render the real dashboard component
 *   'coming-soon' — render the placeholder card listing the planned scope
 */
export type DwaDashboardStatus = 'live' | 'coming-soon';

export interface DwaDashboard {
  slug: string;
  name: string;
  status: DwaDashboardStatus;
  description?: string;
  /** Planned scope, rendered as a list on the coming-soon placeholder. */
  scope?: string[];
  /**
   * Extra Hubs this dashboard also appears in ("also in", docs/handoffs/07-hubs.md §2/§4).
   * Routing is flat (by slug), so a dashboard can be surfaced in more than one
   * Hub without duplicating it. The department it's declared in stays its home
   * Hub — that's what the breadcrumb and access UI use. Values are Hub ids
   * (`src/hubs/hubs.ts`); department slugs double as department Hub ids.
   */
  alsoIn?: string[];
}

export interface DwaDepartment {
  slug: string;
  name: string;
  dashboards: DwaDashboard[];
}

// Alphabetical by department name. Sub-dashboards within a department
// keep their authored order.
export const DWA_INTERNAL_DEPARTMENTS: DwaDepartment[] = [
  {
    slug: 'accounts',
    name: 'Accounts',
    dashboards: [
      {
        slug: 'dwa-accounts-client-onboarding',
        name: 'Client Onboarding',
        status: 'coming-soon',
        description:
          'Intake for a new client, brand, or community — and the checklist that ' +
          'wires it into every part of the platform that needs to know about it. ' +
          'Writes to the Client Database, which is set to become the record (today the ' +
          'details are still spread across the PLC Record of Truth sheet and other places).',
        scope: [
          'Community intake from the ROT field set: identity + status, ownership, ' +
            'community group, community template, site type, server + prod env link, ' +
            'domains, care levels, respite, and the compliance fields (phone, address, ' +
            'state, licensing #, required disclosures).',
          'The Client Database lifecycle statuses: Prospective, Onboarding (with a target ' +
            'date), In development, Live, Offboarding, Inactive, Listing only and ' +
            'Non-community, so in-flight communities are visible before they go live.',
          'Downstream wiring checklist, so nothing lands half-connected: Client Brands ' +
            'entry (template + colors), monitored-site registry for Blog and SmashBalloon, ' +
            'banner targeting, WelcomeHome CRM binding via WH Comm ID, paid-media flags ' +
            'for the Media dashboards, and per-user dashboard access.',
          'Community links surfaced as buttons rather than buried in a sheet: ' +
            'Drive folder, playbook, Google Maps, Facebook, tour and contact forms.',
          'A Google Sheets export of the Client Database, so the ROT sheet becomes a ' +
            'mirror of the platform instead of a second source of truth.',
        ],
        alsoIn: ['web', 'creative', 'media'],
      },
      {
        slug: 'dwa-accounts-client-database',
        name: 'Client Database',
        status: 'live',
        description:
          'The system of record for each client: who they are, what we do for them, ' +
          'and everything the platform needs to know to serve them. Client Onboarding ' +
          'is the intake that populates it; Client Brands is the brand slice of it.',
      },
      {
        // Home is Accounts — it's a system of record about clients, maintained
        // alongside onboarding. Creative are its heaviest consumers, so it's
        // mirrored into that department's sidebar section too.
        slug: 'dwa-creative-client-brands',
        name: 'Client Brands',
        status: 'live',
        description:
          'Client → brand / sub-brand → community hierarchy, with the brand colors ' +
          'that banners and templates read as presets.',
        alsoIn: ['creative'],
      },
    ],
  },
  {
    slug: 'creative',
    name: 'Creative',
    dashboards: [
      {
        slug: 'dwa-creative-scorecard',
        name: 'Creative Scorecard',
        status: 'live',
        description:
          'Project status, hours logged, and team pulse from Teamwork. ' +
          'Refreshed daily at 07:30 UTC.',
        alsoIn: ['operations'],
      },
    ],
  },
  {
    // Home of DWA's own HubSpot (read-only copy, KB hubspot.md). All five render
    // HubSpotDashboardView with a different section; see HUBSPOT_SECTIONS below.
    slug: 'growth',
    name: 'Growth',
    dashboards: [
      {
        slug: 'dwa-growth-deal-pipeline',
        name: 'Deal Pipeline',
        status: 'live',
        description:
          'Open deals by stage per pipeline, weighted forecast, deals closing this month ' +
          'and quarter, and stalled deals. From HubSpot.',
      },
      {
        slug: 'dwa-growth-new-leads',
        name: 'New Leads',
        status: 'live',
        description:
          'New contacts and companies per week for the last 12 weeks, by original source ' +
          'and lifecycle stage. From HubSpot.',
      },
      {
        slug: 'dwa-growth-win-loss',
        name: 'Win / Loss',
        status: 'live',
        description:
          'Deals won vs lost by month, win rate, average won deal size and sales cycle. From HubSpot.',
      },
      {
        slug: 'dwa-growth-owner-activity',
        name: 'Owner Activity',
        status: 'live',
        description:
          'Per deal owner: open pipeline, deals won this quarter, deals created this month ' +
          'and recent calls, meetings and tasks. From HubSpot.',
      },
      {
        slug: 'dwa-growth-crm-health',
        name: 'Lifecycle & CRM Hygiene',
        status: 'live',
        description:
          'Lifecycle funnel with stage-to-stage conversion, plus the deals and contacts ' +
          'missing an amount, close date or owner. From HubSpot.',
      },
    ],
  },
  {
    slug: 'media',
    name: 'Media',
    dashboards: [],
  },
  {
    slug: 'operations',
    name: 'Operations',
    dashboards: [
      {
        slug: 'dwa-operations-team-capacity',
        name: 'Team Capacity',
        status: 'live',
        description:
          'Per-person utilization from Teamwork time entries — logged hours ' +
          'vs available capacity. Refreshed daily at 07:30 UTC.',
        alsoIn: ['creative'],
      },
    ],
  },
  {
    slug: 'web',
    name: 'Web',
    dashboards: [
      {
        slug: 'dwa-web-blog-monitor',
        name: 'Blog Monitor',
        status: 'live',
        description:
          'Latest blog post (date, title, link) per client community website via RSS. ' +
          'Site title links to the homepage. First client: Priority Life Care.',
      },
      {
        slug: 'dwa-web-banners',
        name: 'Banner Manager',
        status: 'live',
        description:
          'Schedule show/hide of site banners across client community websites via the ' +
          'Lupa WordPress shortcode — a cost-free replacement for per-site Opt-In Monster.',
      },
      {
        slug: 'dwa-web-smashballoon',
        name: 'SmashBalloon Feed Monitor',
        status: 'live',
        description:
          'Monitors SmashBalloon Facebook feed plugin status across ~70 client community sites. ' +
          'Surfaces empty/failed feeds so the Web team can fix before clients notice.',
      },
      {
        slug: 'dwa-web-backup-monitor',
        name: 'Backup Monitor',
        status: 'live',
        description:
          'Health of the automated client website backups DWA runs (first: Granite Ridge ' +
          'Builders WordPress media, R2 → Azure). Flags failed, incomplete, or overdue runs.',
        // Web only (Jon, 2026-11-03); it was also listed in Operations.
      },
    ],
  },
];

/**
 * Dashboards to show under a department: the ones it owns, plus any from other
 * departments that list this one in `alsoIn`. Mirrored entries are appended so
 * a department's own dashboards keep their authored order.
 *
 * Listing-only — access management and breadcrumbs use the owning department so
 * a mirrored dashboard doesn't appear twice as a separate permission.
 */
export function dashboardsForDepartment(departmentSlug: string): DwaDashboard[] {
  const own = DWA_INTERNAL_DEPARTMENTS.find((d) => d.slug === departmentSlug)?.dashboards ?? [];
  const mirrored = DWA_INTERNAL_DEPARTMENTS.filter((d) => d.slug !== departmentSlug)
    .flatMap((d) => d.dashboards)
    .filter((d) => d.alsoIn?.includes(departmentSlug));
  return [...own, ...mirrored];
}

export function findDashboard(slug: string): { dashboard: DwaDashboard; department: DwaDepartment } | null {
  for (const department of DWA_INTERNAL_DEPARTMENTS) {
    const dashboard = department.dashboards.find((d) => d.slug === slug);
    if (dashboard) return { dashboard, department };
  }
  return null;
}

export const DEFAULT_DWA_DASHBOARD_SLUG = 'dwa-creative-scorecard';

/** Growth dashboard slug → HubSpotDashboardView section. */
export const HUBSPOT_SECTIONS = {
  'dwa-growth-deal-pipeline': 'pipeline',
  'dwa-growth-new-leads': 'leads',
  'dwa-growth-win-loss': 'win-loss',
  'dwa-growth-owner-activity': 'owners',
  'dwa-growth-crm-health': 'health',
} as const;
